import fb from '@/firebase'
import { UserProfile } from '@/types/users'
import { CollectionNames } from '@/types'
import { Asset, AssetType } from '@/types/assets'
import { CastTemplate } from '@/types/casttemplates'
import { Layout } from '@/types/layouts'
import { Scene } from '@/types/scenes'
import { Region } from '@/types/ops'
import { firestore } from 'firebase'

export type DefaultIdsMapping = {
  layoutIds: Map<string, string>,
  assetIds: Map<string, string>,
  assetGroupIds: Map<string, string>,
  streamIds: Map<string, string>
}

// Changes ids of the default assets to those of the corresponding assets of the team.
const updateContents = (sceneOrLayout: Partial<Scene|Layout>, idsMapping: Partial<DefaultIdsMapping>): void => {
  if (sceneOrLayout?.contents === undefined) return
  for (const [contentId, content] of Object.entries(sceneOrLayout.contents)) {
    let newContentId = idsMapping.assetIds?.get(contentId)
    if (content.type === AssetType.Broadcast) {
      newContentId = idsMapping.streamIds?.get(contentId)
    }
    if (newContentId !== undefined) {
      if (content.type === AssetType.Broadcast && content.name === contentId) {
        content.name = newContentId
      }
      sceneOrLayout.contents[newContentId] = content
      delete sceneOrLayout.contents[contentId]
      content.id = newContentId
    }
  }
}

export const addDefaultLayoutsToTeam = async (teamId: string, idsMapping: Partial<DefaultIdsMapping>): Promise<Partial<DefaultIdsMapping>> => {
  const snap = await fb.db.collection(CollectionNames.LAYOUTS).where('team_id', '==', 'all').get()
  const promises: Promise<void>[] = []
  const layoutIds = new Map<string, string>()
  snap.docs.forEach((doc) => {
    const createLayouts = async () => {
      const layout = doc.data()

      layout.team_id = teamId
      updateContents(layout, idsMapping)
      if (layout.existingLayout !== undefined) updateContents(layout.existingLayout, idsMapping)

      const newLayoutRef = fb.db.collection(CollectionNames.LAYOUTS).doc()
      if (layout.name === 'Fullscreen') {
        await fb.db.collection(CollectionNames.TEAMS).doc(teamId).set({ 'default_layout': newLayoutRef.id }, { merge: true })
      }
      await fb.db.collection(CollectionNames.LAYOUTS).doc(newLayoutRef.id)
                 .set({ ...layout, id: newLayoutRef.id }, { merge: true })
      layoutIds.set(doc.id, newLayoutRef.id)
    }
    promises.push(createLayouts())
  })
  await Promise.all(promises)
  return { layoutIds }
}

export const addDefaultAssetsToTeam = async (teamId: string): Promise<Partial<DefaultIdsMapping>> => {
  const snap = await fb.db.collection(CollectionNames.ASSET_GROUPS).where('team_id', '==', 'all').get()
  const promises: Promise<void>[] = []
  const assetGroupIds = new Map<string, string>()
  snap.docs.forEach((doc) => {
    const createAssetGroups = async () => {
      const assetGroup = doc.data()
      assetGroup.team_id = teamId
      const newAssetGroupRef = fb.db.collection(CollectionNames.ASSET_GROUPS).doc()
      await fb.db.collection(CollectionNames.ASSET_GROUPS).doc(newAssetGroupRef.id)
                 .set({ ...assetGroup, id: newAssetGroupRef.id }, { merge: true })
      assetGroupIds.set(doc.id, newAssetGroupRef.id)
    }
    promises.push(createAssetGroups())
  })
  await Promise.all(promises)

  const assetsnap = await fb.db.collection(CollectionNames.ASSETS).where('team_id', '==', 'all').get()
  const assetpromises: Promise<void>[] = []
  const assetIds = new Map<string, string>()
  assetsnap.docs.forEach((doc) => {
    const createAsset = async () => {
      const asset = doc.data() as Asset
      asset.team_id = teamId
      asset.timestamp = Date.now()
      const assetGroupId = assetGroupIds.get(asset.asset_group)
      if (assetGroupId !== undefined) {
        asset.asset_group = assetGroupId
        const newAssetRef = fb.db.collection(CollectionNames.ASSETS).doc()
        await fb.db.collection(CollectionNames.ASSETS).doc(newAssetRef.id)
                   .set({ ...asset, id: newAssetRef.id }, { merge: true })
        assetIds.set(doc.id, newAssetRef.id)
      }
    }
    assetpromises.push(createAsset())
  })
  await Promise.all(assetpromises)
  return { assetGroupIds, assetIds }
}

export const addDefaultCastTemplatesToTeam = async (teamId: string, idsMapping: DefaultIdsMapping, defaultRegion: Region): Promise<CastTemplate[]> => {
  const snap = await fb.db.collection(CollectionNames.CAST_TEMPLATES).where('team_id', '==', 'all').get()
  const promises: Promise<CastTemplate>[] = []
  snap.docs.forEach((doc) => {
    const createCastTemplates = async () => {
      const castTemplate = doc.data() as CastTemplate
      castTemplate.team_id = teamId

      for (const scene of Object.values(castTemplate.scenes)) {
        updateContents(scene, idsMapping)

        const sceneLayout = scene.scene_layout
        if (sceneLayout.id === undefined) continue

        const newLayoutId = idsMapping.layoutIds.get(sceneLayout.id)
        if (newLayoutId !== undefined) sceneLayout.id = newLayoutId
        sceneLayout.team_id = teamId
        updateContents(sceneLayout, idsMapping)
        if (sceneLayout.existingLayout !== undefined) updateContents(sceneLayout.existingLayout, idsMapping)
      }

      if (castTemplate.asset_groups) {
        for (const assetGroupId of Object.keys(castTemplate.asset_groups)) {
          const newAssetGroupId = idsMapping.assetGroupIds.get(assetGroupId)
          if (newAssetGroupId !== undefined && castTemplate.asset_groups[newAssetGroupId] === undefined) {
            delete castTemplate.asset_groups[assetGroupId]
            castTemplate.asset_groups[newAssetGroupId] = true
          }
        }
      }

      if (castTemplate.input_streams) {
        for (const streamId of Object.keys(castTemplate.input_streams)) {
          const newStreamId = idsMapping.streamIds.get(streamId)
          if (newStreamId !== undefined && castTemplate.input_streams[newStreamId] === undefined) {
            delete castTemplate.input_streams[streamId]
            castTemplate.input_streams[newStreamId] = true
          }
        }
      }

      if (defaultRegion) castTemplate.region = defaultRegion
      castTemplate.created_by = ''
      castTemplate.created_date = firestore.Timestamp.now()

      // Clear any unwanted props.
      castTemplate.invited_casters = {}
      castTemplate.custom_rtmp_destinations = {}
      if (castTemplate.asset_groups === undefined) castTemplate.asset_groups = {}

      const docRef = await fb.db.collection(CollectionNames.CAST_TEMPLATES).add(castTemplate)
      castTemplate.id = docRef.id
      return castTemplate
    }
    promises.push(createCastTemplates())
  })

  return Promise.all(promises)
}

export const removeExpiredNotifications = async (member: UserProfile) => {
  const now = new Date().getTime()
  const expiredKeys = Object.keys(member.notifications).filter(key => parseInt(key,10) < now)
  if (expiredKeys.length) {
    // mark keys for deletion
    const deleteupdate = {}
    expiredKeys.forEach(expiredKey => {
      deleteupdate['notifications.' + expiredKey] = fb.firebase.firestore.FieldValue.delete()
    })

    await fb.db.collection('profiles')
      .doc(member.user_id)
      .update(deleteupdate)

    // console.log('updateUserNotifications | delete DONE ',Object.keys(deleteupdate))
  }
}
